import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import { untilDestroyed } from '@app/core';

import { WebhookEvent, User, WebhookFormatEnum, WebhookEndpoint, WebhookEndpointTypeEnum } from '@app/models';
import { ErrorHandlerService, NotyService, UserService } from '@app/services';
import { TranslateService } from '@ngx-translate/core';
import { WebhooksService } from '@app/services/webhooks.service';
import { EndpointSecretModalService } from '@app/pages/configurations/configurations-webhooks/endpoint-secret-modal/endpoint-secret-modal.service';
import { ClipboardService } from 'ngx-clipboard';

export type ModalResult = {};
export interface ModalPublicProperties {
  endpoint?: WebhookEndpoint;
  isEditingEndpoint?: boolean;
}

@Component({
  selector: 'app-create-endpoint-modal',
  templateUrl: './create-endpoint-modal.component.html',
  styleUrls: ['./create-endpoint-modal.component.scss'],
  standalone: false
})
export class CreateEndpointModalComponent implements ModalPublicProperties, OnInit, OnDestroy {
  @Input() endpoint: WebhookEndpoint;
  @Input() isEditingEndpoint: boolean;
  currentUser: User;
  form: UntypedFormGroup;
  webhookFormat = WebhookFormatEnum.Json;
  webhookFormatEnum = WebhookFormatEnum;
  selectedEventType = WebhookEndpointTypeEnum.Document;
  eventCheckboxOptions: WebhookEvent['events'];
  autentiqueEvents: WebhookEvent[] = [];
  _isLoading = true;
  isLoadingInputs = true;

  eventSelectOptions = [{ key: this.selectedEventType, value: 'Documento', height: 46, fontSize: 16 }];

  hasBearerToken = false;

  readonly fieldsToToggle = ['authorization_token', 'requires_static_ip'];

  constructor(
    public modal: NgbActiveModal,
    public translateService: TranslateService,
    public userService: UserService,
    private formBuilder: UntypedFormBuilder,
    private errorHandlerService: ErrorHandlerService,
    private webhooksService: WebhooksService,
    private endpointSecretService: EndpointSecretModalService,
    private notyService: NotyService,
    private clipboardService: ClipboardService
  ) {
    this.form = this.formBuilder.group({
      url: '',
      name: '',
      type: this.selectedEventType,
      format: this.webhookFormat,
      events: this.formBuilder.array([]),
      authorization_token: '',
      requires_static_ip: false
    });
  }

  ngOnInit() {
    this.isLoading = true;
    this.isLoadingInputs = true;

    this.userService
      .watchCurrentUser()
      .pipe(
        filter(user => !!user),
        untilDestroyed(this),
        tap(user => {
          this.currentUser = user;

          if (!user.subscription.has_premium_features) {
            this.fieldsToToggle.forEach(field => this.form.get(field).disable());
          }
        }),
        switchMap(() => this.webhooksService.getAutentiqueEvents().pipe(finalize(() => (this.isLoadingInputs = false))))
      )
      .subscribe({
        next: data => {
          this.autentiqueEvents = data;
          this.eventCheckboxOptions = this.mapNameToEnumKey();
          this.eventSelectOptions = this.generateOptionsForSelect();
          this.isLoading = false;
          if (this.isEditingEndpoint && this.endpoint) {
            this.populateFormWithEndpointData();
          }
        }
      });

    this.webhooksService.isLoading$.pipe(untilDestroyed(this)).subscribe({
      next: isLoading => {
        this.isLoading = isLoading;
      }
    });
  }

  ngOnDestroy() {}

  populateFormWithEndpointData() {
    this.webhookFormat = this.endpoint.format;
    this.selectedEventType = this.endpoint.type;

    this.form.patchValue({
      url: this.endpoint.url,
      name: this.endpoint.name,
      format: this.webhookFormat,
      type: this.selectedEventType,
      requires_static_ip: this.endpoint.requires_static_ip
    });
    this.hasBearerToken = this.endpoint.has_authorization_token;

    const selectedEvents = this.endpoint.events;
    this.eventsFormArray.clear();
    selectedEvents.forEach((enumKey: string) => {
      this.eventsFormArray.push(this.formBuilder.control(enumKey));
    });
  }

  isEventSelected(enumKey: string): boolean {
    return this.eventsFormArray.value.includes(enumKey);
  }

  onCheckboxChange(event: Event, eventName: string) {
    const inputElement = event.target as HTMLInputElement;
    const enumKey = this.eventCheckboxOptions?.find(ev => ev?.name === eventName)?.enum_key;

    if (inputElement.checked) {
      if (enumKey) {
        this.eventsFormArray.push(this.formBuilder.control(enumKey));
      }
    } else {
      const index = this.eventsFormArray.controls.findIndex(x => x.value === enumKey);
      if (index > -1) {
        this.eventsFormArray.removeAt(index);
      }
    }
  }

  submit() {
    this.form.markAllAsTouched();
    this.isLoadingInputs = true;

    if (!this.hasBearerToken && this.form.get('authorization_token')?.value?.length === 0) {
      this.form.removeControl('authorization_token');
    }

    if (!this.isValidURL(this.form.get('url').value)) {
      this.notyService.error(this.translateService.instant('webhooks.urlFormatError'), 2000);
      return;
    }

    this.isLoading = true;

    if (this.isEditingEndpoint) {
      this.webhooksService
        .updateWebhookEndpoint({
          id: this.endpoint.id,
          ...this.form.getRawValue(),
          keep_authorization_token: this.hasBearerToken
        })
        .pipe(
          switchMap(() => {
            return this.webhooksService.getOrganizationWebhooksEndpoints();
          }),
          finalize(() => (this.isLoadingInputs = false))
        )
        .subscribe({
          next: () => {
            this.notyService.success(this.translateService.instant('webhooks.updateWithSuccess'), 2000);
            this.modal.close({} as ModalResult);
          },
          error: error => this.errorHandlerService.handleValidation(this.form, error)
        });
    } else {
      this.webhooksService
        .createWebhookEndpoint(this.form.getRawValue())
        .pipe(
          tap(() => this.webhooksService.getOrganizationWebhooksEndpoints().subscribe()),
          finalize(() => {
            ((this.isLoadingInputs = false), this.webhooksService.isLoadingSubject.next(false));
          })
        )
        .subscribe({
          next: data => {
            this.endpointSecretService.open({ secret: data.secret }).subscribe();
            this.modal.close({} as ModalResult);
          },
          error: error => this.errorHandlerService.handleValidation(this.form, error)
        });
    }
  }

  generateOptionsForSelect() {
    const options: { key: WebhookEndpointTypeEnum; value: string; height: number; fontSize: number }[] = [];
    this.autentiqueEvents.forEach(ev => {
      options.push({
        key: ev.object,
        value: this.getFriendlyName(ev.object),
        height: 46,
        fontSize: 16
      });
    });
    return options;
  }

  mapNameToEnumKey() {
    let eventsList: WebhookEvent['events'] = [];
    (this.autentiqueEvents || []).forEach(group => (eventsList = eventsList.concat(group?.events || [])));
    return eventsList;
  }

  getFriendlyName(object: string) {
    const mapping: { [key: string]: string } = {
      DOCUMENT: this.translateService.instant('webhooks.document'),
      SIGNATURE: this.translateService.instant('webhooks.signature'),
      MEMBER: this.translateService.instant('webhooks.member'),
      FOLDER: this.translateService.instant('webhooks.folder')
    };

    return mapping[object] || object;
  }

  isValidURL(value: string): boolean {
    try {
      new URL(value);
      return true;
    } catch (error) {
      return false;
    }
  }

  changeEventType(event: Event) {
    this.form.get('type').setValue(event);
    this.eventsFormArray.clear();
  }

  cancel() {
    this.modal.close({} as ModalResult);
  }

  get eventsFormArray(): UntypedFormArray {
    return this.form.get('events') as UntypedFormArray;
  }

  copyStaticIp(ip: string) {
    this.clipboardService.copyFromContent(ip);
    this.notyService.success(this.translateService.instant('shared.textCopySuccess'), 2000);
  }

  get isLoading() {
    return this._isLoading;
  }

  set isLoading(value: boolean) {
    if (value === this.isLoading) return;

    if (this.currentUser?.subscription.has_premium_features) {
      this.fieldsToToggle.forEach(field => this.form.get(field)?.[value ? 'disable' : 'enable']());
    }

    this._isLoading = value;
  }
}
